import type {
  RouteResult,
  RoutingGraph,
  RoutingGraphEdge,
  RoutingGraphNode,
  RoutingMode,
} from "../types/routing";
import { findEdgesForNode, findNodeInGraphs } from "./routingUtils";

/**
 * 優先度付きキュー（最小ヒープ）
 * 一番priorityの値が小さい要素が一番前にいる状態を維持するリスト
 */
class PriorityQueue<T> {
  private heap: Array<{ item: T; priority: number }> = [];

  enqueue(item: T, priority: number): void {
    this.heap.push({ item, priority });
    this.bubbleUp(this.heap.length - 1);
  }

  dequeue(): T | undefined {
    if (this.isEmpty()) return undefined;

    const result = this.heap[0]!; //isEmptyでない
    const end = this.heap.pop();

    if (this.heap.length > 0 && end) {
      this.heap[0] = end;
      this.sinkDown(0);
    }

    return result.item;
  }

  isEmpty(): boolean {
    return this.heap.length === 0;
  }

  private bubbleUp(index: number): void {
    const element = this.heap[index];
    if (element === undefined) return;

    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2);
      const parent = this.heap[parentIndex];

      if (parent === undefined) {
        console.warn("ヒープのbubbleUpで親要素が見つかりませんでした。");
        break;
      }

      if (element.priority >= parent.priority) break;

      this.heap[index] = parent;
      index = parentIndex;
    }

    this.heap[index] = element;
  }

  private sinkDown(index: number): void {
    const length = this.heap.length;
    if (index < 0 || length <= index) return;
    const element = this.heap[index];

    if (element === undefined) {
      console.warn("ヒープのsinkDownで指定された要素がundefinedです。");
      return;
    }

    while (true) {
      let swapIndex = -1;
      const leftIndex = 2 * index + 1;
      const rightIndex = 2 * index + 2;

      if (leftIndex < length) {
        const left = this.heap[leftIndex];

        if (left === undefined) {
          console.warn("ヒープでsinkDwon中に左側の子要素がundefinedでした。");
          break;
        }

        if (left.priority < element.priority) {
          swapIndex = leftIndex;
        }
      }

      if (rightIndex < length) {
        const right = this.heap[rightIndex];

        if (right === undefined) {
          console.warn("ヒープでsinkDwon中に左側の子要素がundefinedでした。");
          break;
        }

        if ((swapIndex !== -1 && swapIndex < 0) || length <= swapIndex) {
          console.warn("ヒープでsinkDown中に範囲外の子要素と交換しようとしています。");
          swapIndex = -1;
        }

        if (
          (swapIndex === -1 && right.priority < element.priority) ||
          (swapIndex !== -1 && right.priority < this.heap[swapIndex]!.priority)
        ) {
          swapIndex = rightIndex;
        }
      }

      if (swapIndex === -1) break;

      if (swapIndex < 0 || length <= swapIndex) {
        console.warn("ヒープでsinkDown中に範囲外の位置から子要素を移動させようとしています。");
        break;
      }

      this.heap[index] = this.heap[swapIndex]!;
      index = swapIndex;
    }

    this.heap[index] = element;
  }
}

/**
 * 経路探索のモードのラッパー関数
 * @param graphs
 * @param startNode
 * @param goalNode
 * @param mode
 * @returns
 */
export const findPath = (
  graphs: Map<string, RoutingGraph>,
  startNode: RoutingGraphNode,
  goalNode: RoutingGraphNode,
  mode: RoutingMode = "default",
): RouteResult | null => {
  if (mode === "default") {
    return findDefaultPath(graphs, startNode, goalNode);
  }
  if (mode === "barrier-free") {
    return findElevatorPath(graphs, startNode, goalNode);
  }

  return null;
};

/**
 * デフォルトでの経路探索
 * @param graphs
 * @param startNode
 * @param goalNode
 * @returns
 */
export const findDefaultPath = (
  graphs: Map<string, RoutingGraph>,
  startNode: RoutingGraphNode,
  goalNode: RoutingGraphNode,
): RouteResult | null => {
  return findShortestPathWithNearestLink(graphs, startNode, goalNode, "default");
};

/**
 * エレベータールート（バリアフリールート）での経路探索
 * @param graphs
 * @param startNode
 * @param goalNode
 * @returns
 */
export const findElevatorPath = (
  graphs: Map<string, RoutingGraph>,
  startNode: RoutingGraphNode,
  goalNode: RoutingGraphNode,
): RouteResult | null => {
  return findShortestPathWithNearestLink(graphs, startNode, goalNode, "barrier-free");
};

/**
 * Dijkstraアルゴリズムで最短経路を探索
 * 一番近い階段・エレベーターを使用
 */
export function findShortestPathWithNearestLink(
  graphs: Map<string, RoutingGraph>,
  startNode: RoutingGraphNode,
  goalNode: RoutingGraphNode,
  mode: RoutingMode = "default",
): RouteResult | null {
  // 優先度付きキュー
  const queue = new PriorityQueue<string>();
  const distances = new Map<string, number>();
  const previous = new Map<string, { nodeId: string; edge: RoutingGraphEdge }>();
  const visited = new Set<string>();

  const startNodeId = startNode.id;
  const goalNodeId = goalNode.id;

  // 初期化
  distances.set(startNodeId, 0);
  queue.enqueue(startNodeId, 0);

  while (!queue.isEmpty()) {
    const currentNodeId = queue.dequeue()!;

    let visitedUniqueValue = `${currentNodeId}_${currentNodeId}`;
    if (previous.has(currentNodeId)) {
      visitedUniqueValue = `${currentNodeId}_${previous.get(currentNodeId)?.nodeId}`;
    }

    if (visited.has(visitedUniqueValue)) continue;
    visited.add(visitedUniqueValue);

    if (currentNodeId === goalNodeId) break;

    const currentDistance = distances.get(currentNodeId)!;

    // 現在のノードのエッジを取得
    const edges = findEdgesForNode(graphs, currentNodeId);

    for (const edge of edges) {
      if (mode === "default" && edge.linkType === "elevator") {
        continue;
      }
      if (mode === "barrier-free" && edge.linkType === "stairs") {
        continue;
      }

      let toLinkDistance = 0;
      if (edge.linkType !== undefined) {
        toLinkDistance = currentDistance + edge.cost;
      }

      const newDistance = currentDistance + edge.cost + toLinkDistance;
      const oldDistance = distances.get(edge.to) ?? Infinity;

      if (newDistance < oldDistance) {
        distances.set(edge.to, newDistance);
        previous.set(edge.to, { nodeId: currentNodeId, edge });
        queue.enqueue(edge.to, newDistance);
      }
    }
  }

  // 経路が見つからない場合
  if (!previous.has(goalNodeId)) {
    return null;
  }

  // 経路を再構築
  const path: RoutingGraphNode[] = [];
  const edges: RoutingGraphEdge[] = [];
  let currentId = goalNodeId;

  while (currentId !== startNodeId) {
    const node = findNodeInGraphs(graphs, currentId);
    if (!node) break;

    path.unshift(node);

    const prev = previous.get(currentId)!;
    edges.unshift(prev.edge);
    currentId = prev.nodeId;
  }

  if (startNode) {
    path.unshift(startNode);
  }

  return {
    nodes: path,
    edges: edges,
    totalCost: distances.get(goalNodeId)!,
  };
}
